import React from "react";
import { Check, X, ArrowRight, List } from "lucide-react";
import { formatOptionLabel, NormalizedOptions } from "../helpers/chatOptions";

interface ChatOptionsBarProps {
  options: NormalizedOptions;
  // Sends the raw (unmodified) value back as the next chat message.
  onSelect: (value: string) => void;
  disabled?: boolean;
}

const ChatOptionsBar: React.FC<ChatOptionsBarProps> = ({
  options,
  onSelect,
  disabled,
}) => {
  if (!options) return null;

  return (
    <div
      role="region"
      aria-label="Quick reply options"
      className="w-full flex flex-col gap-2"
    >
      {options.type === "button" && (
        <div className="flex flex-wrap gap-2 justify-center">
          {options.items.map((value) => {
            const label = formatOptionLabel(value);
            const isYes = value.toLowerCase() === "yes";
            const isNo = value.toLowerCase() === "no";
            return (
              <button
                key={value}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(value)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 dark:border-secondary bg-white dark:bg-primary text-gray-700 dark:text-gray-300 text-sm hover:border-tertiary hover:bg-gray-50 dark:hover:bg-secondary disabled:opacity-50 transition-all shadow-sm"
              >
                {isYes && <Check size={16} className="text-green-500" />}
                {isNo && <X size={16} className="text-red-500" />}
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}

      {options.type === "select" && (
        <div className="w-full flex flex-col gap-1.5">
          <p className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 px-1">
            <List size={14} /> Choose an option
          </p>
          {options.items.map((value, index) => (
            <button
              key={value}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(value)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border border-gray-200 dark:border-secondary bg-white dark:bg-primary text-gray-700 dark:text-gray-300 text-sm text-left hover:border-tertiary hover:bg-gray-50 dark:hover:bg-secondary disabled:opacity-50 transition-all shadow-sm group"
            >
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-secondary text-xs text-gray-500 dark:text-gray-400">
                {index + 1}
              </span>
              <span className="flex-1">{formatOptionLabel(value)}</span>
              <ArrowRight
                size={16}
                className="flex-shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatOptionsBar;
